import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  auth, db,
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged, updateProfile,
  doc, setDoc, getDoc, updateDoc, serverTimestamp
} from './firebase';

const AuthContext = createContext();
const ADMIN_EMAILS = ['admin@freshmart.com'];

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Rebuild user object from Firebase + Firestore ──────────
  const buildUser = async (firebaseUser) => {
    try {
      const snap  = await getDoc(doc(db, 'users', firebaseUser.uid));
      const extra = snap.exists() ? snap.data() : {};
      const role  = ADMIN_EMAILS.includes(firebaseUser.email) ? 'admin' : (extra.role || 'user');
      return {
        uid:       firebaseUser.uid,
        email:     firebaseUser.email,
        name:      extra.name || firebaseUser.displayName || 'User',
        phone:     extra.phone || '',
        address:   extra.address || '',
        city:      extra.city || '',
        pincode:   extra.pincode || '',
        photoURL:  extra.photoURL || firebaseUser.photoURL || '',
        avatar:    (extra.name || firebaseUser.displayName || 'U')[0].toUpperCase(),
        role,
        createdAt: extra.createdAt,
      };
    } catch {
      return {
        uid:      firebaseUser.uid,
        email:    firebaseUser.email,
        name:     firebaseUser.displayName || 'User',
        avatar:   'U',
        photoURL: '',
        role:     ADMIN_EMAILS.includes(firebaseUser.email) ? 'admin' : 'user',
      };
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const u = await buildUser(firebaseUser);
        setUser(u);
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // ── SIGNUP ──────────────────────────────────────────────────
  const signup = async (name, email, password) => {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name });
      const role = ADMIN_EMAILS.includes(email) ? 'admin' : 'user';
      await setDoc(doc(db, 'users', cred.user.uid), {
        uid: cred.user.uid, name, email, role,
        phone: '', address: '', city: '', pincode: '', photoURL: '',
        createdAt: serverTimestamp(),
      });
      return { success: true };
    } catch (err) {
      const msg =
        err.code === 'auth/email-already-in-use' ? 'Email already registered' :
        err.code === 'auth/weak-password'         ? 'Password must be 6+ characters' :
        err.code === 'auth/invalid-email'         ? 'Invalid email address' :
        err.message;
      return { success: false, error: msg };
    }
  };

  // ── LOGIN ────────────────────────────────────────────────────
  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      const role = ADMIN_EMAILS.includes(email) ? 'admin' : 'user';
      return { success: true, role };
    } catch (err) {
      const msg =
        err.code === 'auth/user-not-found'    ? 'No account with this email' :
        err.code === 'auth/wrong-password'    ? 'Incorrect password' :
        err.code === 'auth/invalid-email'     ? 'Invalid email address' :
        err.code === 'auth/invalid-credential'? 'Invalid email or password' :
        err.code === 'auth/too-many-requests' ? 'Too many attempts. Try later.' :
        'Login failed. Check credentials.';
      return { success: false, error: msg };
    }
  };

  // ── UPDATE PROFILE ───────────────────────────────────────────
  const updateUserProfile = async (updates) => {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) return { success: false, error: 'Not logged in' };

      // Update Firebase Auth displayName & photoURL
      const authUpdates = {};
      if (updates.name)     authUpdates.displayName = updates.name;
      if (updates.photoURL) authUpdates.photoURL    = updates.photoURL;
      if (Object.keys(authUpdates).length) {
        await updateProfile(firebaseUser, authUpdates);
      }

      // Update Firestore user doc
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      // Refresh local user state
      const refreshed = await buildUser(firebaseUser);
      setUser(refreshed);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // ── LOGOUT ───────────────────────────────────────────────────
  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, updateUserProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
